import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  dataToLovs,
  dataToPlanLovs,
  dataToSublineLovs,
  extractAssignedBPWithPagination,
} from './utils';
import { isEqual } from 'lodash';
import { toast } from 'react-toastify';
import { useAppSelector } from '@redux/hooks';
import { getFilter, setFilter } from '@utils/filter-utils';
import { IListingData } from '@models/listing';
import { DEFAULT_ERROR_TEXT } from '@utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ListingFilterWidget from '@components/widgets/listing-filter-widget/ListingFilterWidget';
import StaticLayout from '@components/layout/StaticLayout';
import DateService from '@services/DateService';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import ListingWidget from '@components/widgets/listing-widget/ListingWidget';
import { IFilterModel } from '@components/widgets/listing-widget';
import { mapToListingData } from './utils';
import {
  filterQuery,
  getAssignedBusinessPartnersQuery,
  getAssignedLinesList,
  getLatestPolicyList,
} from './queries';
import { filterSectionsContent, getHeaders } from './content';
import { DEFAULT_FILTER_LOVS, IPolicyFilterLovs } from '.';
import { isEmpty } from '@utils/validationUtils';

const PAGE_CONTEXT = 'GroupPolicies';
const FILTER_SESSION_KEY = 'groupPolicyFilter';

const GroupPoliciesListingPage: React.FC = () => {
  const user = useAppSelector((state) => state.user);
  const businessUserId = user.info.businessUser_Id;

  const initialFilterValues =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY)
      : {
          line: [],
          subline: [],
          plan: [],
          businessPartner: [],
          insured: [],
          SelectedStatuses: [],
          SelectedStatusReason: [],
          effectiveDate: [],
          issueDate: [],
        };

  const [filterValues, setFilterValues] = useState<IFilterModel>({
    namedFilters: initialFilterValues,
    pagination: {
      pageSize: 10,
      pageNumber: 1,
    },
    descending: false,
  });

  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 1,
    totalCount: 0,
  });

  const [filterSections, setFilterSections] =
    useState<IListingFilterWidgetSection[]>();
  const [lovs, setLovs] = useState<IPolicyFilterLovs>(DEFAULT_FILTER_LOVS);
  const [getProductionPoliciesLazy, { loading: loadingTable }] =
    useLazyQuery(getLatestPolicyList);
  const [getFiltersLovs, { loading: lovsLoading }] = useLazyQuery(filterQuery);
  const [filterLineLazy] = useLazyQuery(getAssignedLinesList);
  const [getAssignedBusinessPartnersLazy] = useLazyQuery(
    getAssignedBusinessPartnersQuery
  );

  const initialize = async () => {
    Promise.all([
      getFiltersLovs({
        variables: {
          selectedBusinessUserID: businessUserId,
          selectedLineID: filterValues.namedFilters.line,
        },
      }),
      filterLineLazy({
        variables: {
          selectedBusinessUserID: businessUserId,
        },
      }),
    ]).then(([{ data: filtersData }, { data: lineFiltersData }]) => {
      const lovs = dataToLovs(
        filtersData,
        lineFiltersData,
        filterValues.namedFilters
      );
      setLovs(lovs);
      setFilterSections(filterSectionsContent(lovs, filterValues.namedFilters));
    });
  };

  const onFilterUpdate = async (
    filters: Record<string, unknown>,
    inputName?: string
  ) => {
    if (isEqual(filters, filterValues.namedFilters)) {
      // Do nothing if filters are the same
      return;
    }
    const newFilters = {
      ...filterValues,
      namedFilters: filters,
    };
    setFilter(filters, FILTER_SESSION_KEY);
    setFilterValues(newFilters);
    getListingData(newFilters);
    if (inputName === 'line') {
      getFiltersLovs({
        variables: {
          selectedBusinessUserID: businessUserId,
          selectedLineID: filters.line,
        },
      }).then(({ data: filtersData }) => {
        const sublines = dataToSublineLovs(filtersData);
        const plans = dataToPlanLovs(
          filtersData,
          filters.subline?.toString(),
          sublines
        );
        const newLovs = {
          ...lovs,
          subline: sublines,
          plan: plans,
        };
        setLovs(newLovs);
        setFilterSections(filterSectionsContent(newLovs, filters));
      });
    }
  };

  const getListingData = (filter = filterValues) => {
    const filterV = filter.namedFilters;
    const pagination = filter.pagination;
    const formattedEffectiveDateRange = DateService.formatDateRangeBackend(
      filterV.effectiveDate
    );
    const formattedIssueDateRange = DateService.formatDateRangeBackend(
      filterV.issueDate
    );
    return getProductionPoliciesLazy({
      variables: {
        currentPage: pagination.pageNumber,
        currentPageSize: pagination.pageSize,
        KeywordSearch: isEmpty(filter.searchKey) ? null : filter.searchKey,
        OrderBy: {
          Attribute: isEmpty(filter.orderBy)
            ? 'Production_Policy_PolicyIssueDate'
            : filter.orderBy,
          Descending: isEmpty(filter.orderBy) ? true : filter.descending,
        },
        selectedLines: filterV?.line?.length ? filterV.line : [],
        selectedSublines: filterV?.subline?.length ? filterV.subline : [],
        selectedPlans: filterV?.plan?.length ? filterV.plan : [],
        selectedBusinessPartners: filterV?.businessPartner?.length
          ? filterV.businessPartner
          : [],
        selectedInsuredIDs: filterV?.insured?.length ? filterV.insured : [],
        SelectedStatuses: filterV?.status?.length ? filterV.status : null,
        selectedStatusReasons: filterV?.statusReason?.length
          ? filterV.statusReason
          : null,
        effectiveDateFrom: formattedEffectiveDateRange?.[0],
        effectiveDateTo: formattedEffectiveDateRange?.[1],
        issueDateFrom: formattedIssueDateRange?.[0],
        issueDateTo: formattedIssueDateRange?.[1],
        type: 'GROUP',
      },
    })
      .then(({ data: policiesData }) => {
        if (policiesData.error) {
          toast.error(
            <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
          );
          return;
        }

        setFilterValues(filter);

        // Update table data
        const newTableData = mapToListingData(policiesData);
        setTableData(newTableData);
      })
      .catch(() => {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      });
  };

  function handlePageChange(
    page: number,
    filterModel = filterValues
  ): Promise<void> {
    return new Promise<void>(() =>
      getListingData({
        ...filterModel,
        pagination: {
          ...filterModel.pagination,
          pageNumber: page,
        },
      })
    );
  }

  const renderMainChildren = () => (
    <ListingWidget
      title="Group Policies"
      name="groupPolicies"
      data={tableData}
      actions={[]}
      defaultOrderByColumn="Production_Policy_PolicyIssueDate"
      tableSettings={{ headers: getHeaders('group') }}
      loading={loadingTable}
      pageContext={PAGE_CONTEXT}
      onPageChange={(filterModel) => {
        const newFilterModel = {
          ...filterValues,
          ...filterModel,
        };
        const page = filterModel.pagination.pageNumber;
        return handlePageChange(page, newFilterModel);
      }}
      usePagination
      disableSelection
    />
  );

  const renderFilter = () => (
    <ListingFilterWidget
      name=""
      disabled={lovsLoading}
      filters={filterSections}
      loading={!filterSections}
      onApplyFilter={onFilterUpdate}
      onAutocompleteSearch={(fieldName: string, value: string, pagination) => {
        if (fieldName === 'businessPartner') {
          return getAssignedBusinessPartnersLazy({
            variables: {
              selectedBusinessUserID: businessUserId,
              searchKeyword: value,
              pagination,
            },
          }).then((result) => {
            return {
              options: extractAssignedBPWithPagination(result.data),
              totalCount:
                result.data.SalesforceManagement.queries.getAssignedBPList
                  .paging.totalCount,
            };
          });
        }

        return new Promise<{
          options: Record<string, string>;
          totalCount: number;
        }>((resolve) => {
          resolve({ options: {}, totalCount: 0 });
        });
      }}
    />
  );

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StaticLayout
      name="GroupPoliciesListingPage"
      leftChildren={renderFilter()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default GroupPoliciesListingPage;
